// Contexte d'un signalement : où on est, avec quoi, en ligne ou non. Tout ce
// qui varie selon l'environnement d'exécution (location, navigator, écran…)
// est INJECTÉ — lireContexte ne touche à aucun global — pour rester testable
// sans jsdom réel et sans effet caché.

export interface Contexte {
  url: string;
  chemin: string;
  navigateur: string;
  agent: string;
  langue: string;
  ecran: string;
  fenetre: string;
  pixelRatio: number;
  enLigne: boolean;
  heure: string;
  fuseau: string;
  version: string;
  natif: boolean;
}

export interface EnvironnementContexte {
  location: Location;
  navigator: Navigator;
  screen: Screen;
  innerWidth: number;
  innerHeight: number;
  devicePixelRatio: number;
  now: Date;
  version: string;
  natif: boolean;
}

export function lireContexte(env: EnvironnementContexte): Contexte {
  return {
    url: env.location.href,
    chemin: env.location.pathname,
    navigateur: navigateurDepuisAgent(env.navigator.userAgent),
    agent: env.navigator.userAgent,
    langue: env.navigator.language,
    ecran: `${env.screen.width}x${env.screen.height}`,
    fenetre: `${env.innerWidth}x${env.innerHeight}`,
    pixelRatio: env.devicePixelRatio,
    enLigne: env.navigator.onLine,
    heure: env.now.toISOString(),
    fuseau: Intl.DateTimeFormat().resolvedOptions().timeZone,
    version: env.version,
    natif: env.natif,
  };
}

function detecterOs(ua: string): string | null {
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Macintosh|Mac OS X/.test(ua)) return "macOS";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  return null;
}

function detecterNavigateur(ua: string): { nom: string; version: string } | null {
  const estEdgeOuOpera = /Edg\//.test(ua) || /OPR\//.test(ua);
  if (!estEdgeOuOpera) {
    const chrome = ua.match(/Chrome\/(\d+)/);
    if (chrome?.[1]) return { nom: "Chrome", version: chrome[1] };
  }
  const estChromeOuCriOS = /Chrome\//.test(ua) || /CriOS\//.test(ua);
  if (/Safari\//.test(ua) && !estChromeOuCriOS) {
    const safari = ua.match(/Version\/(\d+)/);
    if (safari?.[1]) return { nom: "Safari", version: safari[1] };
  }
  return null;
}

// "Safari 26 sur iOS" | "Chrome 128 sur macOS" | "Navigateur inconnu" —
// détection minimale (Chrome/Safari, iOS/macOS/Android/Windows) : suffisante
// pour un fil de débogage, pas un remplacement d'une bibliothèque UA complète.
export function navigateurDepuisAgent(ua: string): string {
  const os = detecterOs(ua);
  const navigateur = detecterNavigateur(ua);
  if (!os || !navigateur) return "Navigateur inconnu";
  return `${navigateur.nom} ${navigateur.version} sur ${os}`;
}
